import {
  DatePicker as DatePickerCarbon,
  DatePickerProps as DatePickerPropsCarbon,
  DatePickerInput,
  DatePickerInputProps,
  Layer,
} from '@carbon/react';
import classNames from 'classnames';
import { format, parse } from 'date-fns';
import { ChangeEventHandler, ReactNode } from 'react';
import style from './date-picker.module.scss';

export interface DatePickerProps extends Omit<
  DatePickerPropsCarbon,
  'value' | 'dateFormat' | 'children'
> {
  labelText: string;
  invalid?: boolean;
  invalidText?: ReactNode;
  id: string;
  disabled?: boolean;
  value?: string;
  size?: DatePickerInputProps['size'];
  /**
   * Native input autocomplete. Defaults to 'off': this is a typed-date field with
   * a flatpickr calendar, and the browser's stored-value dropdown overlaps the
   * calendar and offers irrelevant suggestions. Pass a value to override.
   */
  autoComplete?: string;
  /**
   * Fires on raw keystrokes in the text input. flatpickr only emits `onChange`
   * on calendar-select or Enter, so this lets callers capture manually typed
   * dates (which are otherwise discarded on blur).
   */
  onInputChange?: ChangeEventHandler<HTMLInputElement>;
}

const DatePicker = ({
  light = false,
  size = 'lg',
  id,
  value,
  onChange,
  labelText,
  invalid,
  invalidText,
  disabled,
  autoComplete = 'off',
  onInputChange,
  ...rest
}: DatePickerProps) => {
  const className = classNames(style.datePicker, {
    [style.markedForDestruction]: disabled,
  });

  const dateValue = value
    ? format(parse(value, 'yyyy-MM-dd', new Date()), 'MM/dd/yyyy')
    : undefined;

  const datePicker = (
    <DatePickerCarbon
      dateFormat="m/d/Y"
      datePickerType="single"
      onChange={onChange}
      value={dateValue}
      className={className}
      {...rest}
    >
      <DatePickerInput
        id={id}
        placeholder="mm/dd/yyyy"
        labelText={labelText}
        invalid={invalid}
        invalidText={invalidText as string}
        size={size}
        disabled={disabled}
        onChange={onInputChange}
        {...({ autoComplete } as { autoComplete?: string })}
      />
    </DatePickerCarbon>
  );

  return light ? <Layer>{datePicker}</Layer> : datePicker;
};

export { DatePicker };
